import { SortDirection } from '@angular/material/sort';

import { createReducer, Action, on } from '@ngrx/store';

import { TransactionActions } from './transaction.actions';
import { TransactionState } from './transaction.state';
import { arrayToFixedValueRecord } from '../../features/shared/utils/array-to-record';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { AppActions } from '../app/app.actions';
import { EmbeddedActions } from '../embedded/embedded.actions';
import { Transaction } from '../../features/home/models/transaction.model';

export const initialState: TransactionState = {
  loading: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      AppActions.LoadInitData,
      TransactionActions.UpsertTransaction,
      TransactionActions.DeleteTransaction,
      (state) => ({
        ...state,
        loading: true,
        error: undefined,
      })
    ),
    on(AppActions.LoadInitDataSuccess, (state) => ({ ...state, loading: false, error: undefined })),
    on(TransactionActions.LoadList, (state) => ({ ...state, loading: true, error: undefined })),
    on(TransactionActions.LoadTransactionsSuccess, (state, action): TransactionState => {
      if (!action.transactions.length) {
        return { ...state };
      }

      const newIdLookup = arrayToFixedValueRecord(action.transactions, true, 'transactionId');

      const list = [...(state.list?.filter((t) => !newIdLookup[t.transactionId]) || []), ...action.transactions];

      const lastUpdatedAt = list.reduce((acc, t) => {
        return Math.max(acc, new Date(t.updatedAt || 0).getTime());
      }, 0);

      return {
        ...state,
        list,
        lastUpdatedAt: new Date(lastUpdatedAt).toISOString(),
      };
    }),
    on(AppActions.LoadInitDataSuccess, (state, action): TransactionState => {
      const lastUpdatedAt = (action?.data?.transactions || []).reduce((acc, t) => {
        return Math.max(acc, new Date(t.updatedAt || 0).getTime());
      }, 0);

      return {
        ...state,
        loading: false,
        list: action.data.transactions,
        lastUpdatedAt: new Date(lastUpdatedAt).toISOString(),
      };
    }),
    on(TransactionActions.LoadListSuccess, (state, action) => ({
      ...state,
      loading: false,
      list: action.list,
    })),
    on(EmbeddedActions.LoadDealSuccess, (state, action) => ({
      ...state,
      loading: false,
      list: [action.deal],
    })),
    on(
      TransactionActions.LoadListFail,
      TransactionActions.UpsertTransactionFail,
      TransactionActions.DeleteTransactionFail,
      (state, action) => ({ ...state, loading: false, error: action.error })
    ),
    on(TransactionActions.Sort, (state, action) => ({ ...state, sort: action.sort })),
    on(TransactionActions.Select, (state, action) => ({
      ...state,
      selectedId: action.transactionId,
    })),
    on(TransactionActions.Search, (state, action) => ({
      ...state,
      search: action.search,
    })),
    on(TransactionActions.ClearModifiers, (state) => ({
      ...state,
      sort: {
        active: 'transactionDisplayClosingDate',
        direction: 'desc' as SortDirection,
      },
      search: undefined,
    })),
    on(TransactionActions.DeleteTransactionSuccess, (state, action) => ({
      ...state,
      loading: false,
      selectedId: state.selectedId === action.transaction.transactionId ? undefined : state.selectedId,
      list: state?.list?.filter((t) => t.transactionId !== action.transaction.transactionId),
    })),
    on(TransactionActions.UpsertTransactionSuccess, (state, action) => ({
      ...state,
      loading: false,
      selectedId: action.transaction.transactionId,
      list: [
        ...(state?.list || []).filter((t) => t.transactionId !== action.transaction.transactionId),
        action.transaction,
      ],
    })),
    on(TransactionActions.PollingTransactionChangesSuccess, (state, action) => {
      if (!action.transactions.length) {
        // Do not update state if no transactions are returned
        return state;
      }

      const listLookup = new Map<string, Transaction>(
        [...(state?.list || [])].map((t): [string, Transaction] => [t.transactionId, t])
      );

      const updatedListLookup = action.transactions.reduce((acc, t) => {
        acc.set(t.transactionId, t);
        return acc;
      }, listLookup);

      const list = Array.from(updatedListLookup.values());

      const lastUpdatedAt = list.reduce((acc, t) => {
        return Math.max(acc, new Date(t.updatedAt || 0).getTime());
      }, 0);

      return {
        ...state,
        list,
        lastUpdatedAt: new Date(lastUpdatedAt).toISOString(),
      };
    }),
    on(TransactionActions.FullRefreshTransactionsSuccess, (state, action): TransactionState => {
      if (!action.transactions.length) {
        // Never wipe the list on an empty full-refresh response — keep the current state
        return state;
      }

      const lastUpdatedAt = action.transactions.reduce((acc, t) => {
        return Math.max(acc, new Date(t.updatedAt || 0).getTime());
      }, 0);

      return {
        ...state,
        list: action.transactions,
        lastUpdatedAt: new Date(lastUpdatedAt).toISOString(),
      };
    })
  )
);

export function reducer(state: TransactionState | undefined, action: Action) {
  return featureReducer(state, action);
}
